/**
 * Mock host bootstrap.
 *
 * Order is load-bearing:
 *   1. Register the MSW service worker. Once active it intercepts every
 *      `/api/**` request the uiv2 app (and integration remotes) make. No
 *      real backend is reachable from this point on.
 *   2. Mount the demo overlay into its own DOM root.
 *   3. Dynamically import uiv2's compiled entry. Up to this point uiv2
 *      hasn't run yet — its first fetch will be intercepted by MSW.
 *
 * `data-uiv2-entry` on the host <script> tag tells us which hashed asset
 * file to import. `scripts/build.ts` patches it into the served
 * index.html after extracting the entry filename from uiv2's own build
 * output. That keeps uiv2 hash-agnostic on our side.
 *
 * If MSW fails to register we abort instead of letting uiv2 boot. The
 * whole point of this host is "no network", so a half-mocked state is
 * worse than no demo.
 */

import { startMockWorker } from "@/mocks/browser";
import { mountDemoOverlay } from "./demo-overlay";

function readUiv2EntryFromScriptTag(): string {
  // `document.currentScript` is always null inside ES modules, so we
  // can't use it. Find our own <script> tag by its src attribute and
  // read the data-uiv2-entry attribute from there. scripts/build.ts
  // emits exactly one such tag, so this is unambiguous.
  const el = document.querySelector<HTMLScriptElement>(
    'script[src="/msw-bootstrap.js"]',
  );
  const entry = el?.dataset.uiv2Entry;
  if (!entry) {
    throw new Error(
      `Bootstrap script missing data-uiv2-entry attribute (script tag: ${
        el ? "found but no dataset" : "not found in DOM"
      }). Did scripts/build.ts run?`,
    );
  }
  return entry;
}

function renderFailureScreen(err: unknown) {
  // eslint-disable-next-line no-console
  console.error("[mock-host] bootstrap failed", err);
  const root =
    document.getElementById("root") ||
    document.body.appendChild(document.createElement("div"));
  root.innerHTML = `
    <div style="font-family: system-ui; padding: 2rem; color: #fca5a5; background: #1f0a0a; min-height: 100vh;">
      <h1 style="margin-top: 0;">Demo failed to start</h1>
      <p>The mock host could not initialise MSW or load uiv2. Check the browser console.</p>
      <pre style="background: #000; padding: 1rem; overflow: auto; color: #fca5a5; white-space: pre-wrap;">${String(err)}</pre>
    </div>
  `;
}

async function bootstrap() {
  const uiv2Entry = readUiv2EntryFromScriptTag();

  await startMockWorker();
  mountDemoOverlay();

  // Use vite-ignore so the bundler doesn't try to resolve uiv2's hashed
  // entry path at our build time — that file only exists in the composed
  // dist/, after `scripts/build.ts` runs.
  await import(/* @vite-ignore */ uiv2Entry);
}

bootstrap().catch(renderFailureScreen);
